import { useState } from 'react'
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tabs,
    Tab,
    CircularProgress,
    Tooltip,
    Alert,
} from '@mui/material'
import {
    Check,
    Close,
    Delete,
    Refresh,
    HourglassEmpty,
    CheckCircle,
    Cancel,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@api/axios'
import { useTranslation } from '@i18n'
import ConfirmDialog from '@components/Common/ConfirmDialog'
import { toast } from 'react-toastify'
import { format } from 'date-fns'

const Registrations = () => {
    const { t, language } = useTranslation()
    const queryClient = useQueryClient()
    const isRTL = language === 'ar'

    const [tab, setTab] = useState('pending')
    const [rejectDialog, setRejectDialog] = useState({ open: false, id: null })
    const [rejectReason, setRejectReason] = useState('')
    const [confirmAction, setConfirmAction] = useState({ open: false, type: '', id: null, name: '' })

    // Fetch registrations
    const { data: registrations, isLoading, refetch } = useQuery({
        queryKey: ['registrations', tab],
        queryFn: async () => {
            const response = await api.get(`/admin/registrations?status=${tab}`)
            return response.data
        },
    })

    // Fetch counts
    const { data: counts } = useQuery({
        queryKey: ['registrations-counts'],
        queryFn: async () => {
            const response = await api.get('/admin/registrations/counts')
            return response.data
        },
    })

    // Approve mutation
    const approveMutation = useMutation({
        mutationFn: (id) => api.post(`/admin/registrations/${id}/approve`),
        onSuccess: () => {
            toast.success(isRTL ? 'تم قبول التسجيل' : 'Registration approved')
            queryClient.invalidateQueries(['registrations'])
            queryClient.invalidateQueries(['registrations-counts'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to approve')
        },
    })

    // Reject mutation
    const rejectMutation = useMutation({
        mutationFn: ({ id, reason }) => api.post(`/admin/registrations/${id}/reject`, { reason }),
        onSuccess: () => {
            toast.success(isRTL ? 'تم رفض التسجيل' : 'Registration rejected')
            setRejectDialog({ open: false, id: null })
            setRejectReason('')
            queryClient.invalidateQueries(['registrations'])
            queryClient.invalidateQueries(['registrations-counts'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reject')
        },
    })

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => api.delete(`/admin/registrations/${id}`),
        onSuccess: () => {
            toast.success(isRTL ? 'تم حذف التسجيل' : 'Registration deleted')
            queryClient.invalidateQueries(['registrations'])
            queryClient.invalidateQueries(['registrations-counts'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete')
        },
    })

    const handleApprove = (id) => {
        setConfirmAction({ open: true, type: 'approve', id, name: '' })
    }

    const handleReject = () => {
        rejectMutation.mutate({ id: rejectDialog.id, reason: rejectReason })
    }

    const handleDelete = (id) => {
        setConfirmAction({ open: true, type: 'delete', id, name: '' })
    }

    const handleConfirmAction = () => {
        if (confirmAction.type === 'approve') {
            approveMutation.mutate(confirmAction.id)
        } else if (confirmAction.type === 'delete') {
            deleteMutation.mutate(confirmAction.id)
        }
        setConfirmAction({ open: false, type: '', id: null, name: '' })
    }

    const getStatusChip = (status) => {
        const configs = {
            pending: { color: 'warning', icon: <HourglassEmpty fontSize="small" />, label: isRTL ? 'قيد الانتظار' : 'Pending' },
            approved: { color: 'success', icon: <CheckCircle fontSize="small" />, label: isRTL ? 'مقبول' : 'Approved' },
            rejected: { color: 'error', icon: <Cancel fontSize="small" />, label: isRTL ? 'مرفوض' : 'Rejected' },
        }
        const config = configs[status] || configs.pending
        return <Chip size="small" icon={config.icon} label={config.label} color={config.color} />
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '-'
        try {
            return format(new Date(dateStr), 'yyyy-MM-dd HH:mm')
        } catch {
            return dateStr
        }
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight={700}>
                    {isRTL ? 'إدارة التسجيلات' : 'User Registrations'}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                >
                    {isRTL ? 'تحديث' : 'Refresh'}
                </Button>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                    <Tab
                        value="pending"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HourglassEmpty />
                                {isRTL ? 'قيد الانتظار' : 'Pending'}
                                {counts?.pending > 0 && (
                                    <Chip size="small" label={counts.pending} color="warning" />
                                )}
                            </Box>
                        }
                    />
                    <Tab
                        value="approved"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle />
                                {isRTL ? 'مقبول' : 'Approved'}
                                {counts?.approved > 0 && (
                                    <Chip size="small" label={counts.approved} color="success" />
                                )}
                            </Box>
                        }
                    />
                    <Tab
                        value="rejected"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Cancel />
                                {isRTL ? 'مرفوض' : 'Rejected'}
                                {counts?.rejected > 0 && (
                                    <Chip size="small" label={counts.rejected} color="error" />
                                )}
                            </Box>
                        }
                    />
                </Tabs>
            </Paper>

            {/* Table */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : registrations?.length === 0 ? (
                <Alert severity="info">
                    {isRTL ? 'لا توجد تسجيلات' : 'No registrations found'}
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{isRTL ? 'الاسم' : 'Name'}</TableCell>
                                <TableCell>{isRTL ? 'البريد الإلكتروني' : 'Email'}</TableCell>
                                <TableCell>{isRTL ? 'الهاتف' : 'Phone'}</TableCell>
                                <TableCell>{isRTL ? 'النموذج' : 'Form'}</TableCell>
                                <TableCell>{isRTL ? 'تاريخ التسجيل' : 'Registration Date'}</TableCell>
                                <TableCell>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                                <TableCell align="center">{isRTL ? 'الإجراءات' : 'Actions'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {registrations?.map((reg) => (
                                <TableRow key={reg.id}>
                                    <TableCell>{reg.full_name}</TableCell>
                                    <TableCell>{reg.email}</TableCell>
                                    <TableCell>{reg.phone || '-'}</TableCell>
                                    <TableCell>
                                        {isRTL ? reg.form_title_ar || reg.form_title : reg.form_title || '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(reg.created_at)}</TableCell>
                                    <TableCell>{getStatusChip(reg.status)}</TableCell>
                                    <TableCell align="center">
                                        {reg.status === 'pending' && (
                                            <>
                                                <Tooltip title={isRTL ? 'قبول' : 'Approve'}>
                                                    <IconButton
                                                        color="success"
                                                        onClick={() => handleApprove(reg.id)}
                                                        disabled={approveMutation.isPending}
                                                    >
                                                        <Check />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isRTL ? 'رفض' : 'Reject'}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => setRejectDialog({ open: true, id: reg.id })}
                                                        disabled={rejectMutation.isPending}
                                                    >
                                                        <Close />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                        <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDelete(reg.id)}
                                                disabled={deleteMutation.isPending}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Reject Dialog */}
            <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: null })}>
                <DialogTitle>{isRTL ? 'رفض التسجيل' : 'Reject Registration'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={isRTL ? 'سبب الرفض (اختياري)' : 'Rejection Reason (optional)'}
                        fullWidth
                        multiline
                        rows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog({ open: false, id: null })}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleReject}
                        color="error"
                        variant="contained"
                        disabled={rejectMutation.isPending}
                    >
                        {isRTL ? 'رفض' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmAction.open}
                title={
                    confirmAction.type === 'approve'
                        ? (isRTL ? 'تأكيد القبول' : 'Confirm Approval')
                        : (isRTL ? 'تأكيد الحذف' : 'Confirm Delete')
                }
                message={
                    confirmAction.type === 'approve'
                        ? (isRTL ? 'هل تريد قبول هذا التسجيل؟' : 'Approve this registration?')
                        : (isRTL ? 'هل تريد حذف هذا التسجيل نهائياً؟' : 'Permanently delete this registration?')
                }
                confirmText={
                    confirmAction.type === 'approve'
                        ? (isRTL ? 'قبول' : 'Approve')
                        : (isRTL ? 'حذف' : 'Delete')
                }
                cancelText={isRTL ? 'إلغاء' : 'Cancel'}
                color={confirmAction.type === 'approve' ? 'success' : 'error'}
                onConfirm={handleConfirmAction}
                onCancel={() => setConfirmAction({ open: false, type: '', id: null, name: '' })}
            />
        </Box>
    )
}

export default Registrations
